'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Container, Typography, TextField, Button, Box, IconButton, Paper, CircularProgress, Alert, Chip, LinearProgress, Switch, FormControlLabel, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, Tooltip, Fade } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Refresh as RefreshIcon, Upload as UploadIcon, Article as ArticleIcon, Image as ImageIcon, Title as TitleIcon, Link as LinkIcon, ContentPaste as ContentPasteIcon, Category as CategoryIcon, DateRange as DateRangeIcon, DragIndicator as DragIcon, FormatBold as BoldIcon, FormatItalic as ItalicIcon } from '@mui/icons-material';
import { BlogPost, Venue } from '../../../../../shared/models';
import { createBlogPost, updateBlogPost, getBlogPost, uploadBlogImage, uploadMultipleBlogImages, deleteBlogImage } from '../../../lib/firebase/blog';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { useRouter } from 'next/navigation';
import SelectImageFromVenue from '../select_image_from_venue';
import { FetchAllVenues } from '../../../lib/firebase/venues';
import OptimizedImage from '../../../components/OptimizedImage';
import { getBlogPostCategories, addBlogPostCategory } from '../../../lib/firebase/metadata';
import { useAuthState } from '../../../lib/firebase/app';
import { getUserRoles, UserRoles } from '../../../lib/firebase/auth';

interface BlogEditorProps {
    blogId?: string;
    isNewPost?: boolean;
}

const BlogEditor = ({ blogId, isNewPost = false }: BlogEditorProps) => {
    const router = useRouter();
    const { user, loading: authLoading } = useAuthState();
    const [userRoles, setUserRoles] = useState<UserRoles | null>(null);
    const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

    const [loading, setLoading] = useState(true);
    const [uploadingHeadlineImage, setUploadingHeadlineImage] = useState(false);
    const [uploadingContentImage, setUploadingContentImage] = useState<number | null>(null);
    const [headlineUploadProgress, setHeadlineUploadProgress] = useState(0);
    const [contentUploadProgress, setContentUploadProgress] = useState<{ [key: number]: number }>({});
    const [error, setError] = useState<string | null>(null);
    const [selectImageDialogOpen, setSelectImageDialogOpen] = useState(false);
    const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
    const [selectedVenueImages, setSelectedVenueImages] = useState<string[]>([]);
    const [venues, setVenues] = useState<Venue[]>([]);
    const [post, setPost] = useState<BlogPost>({
        ID: '',
        Title: '',
        Subtitle: '',
        Content: [],
        Excerpt: '',
        PublishDate: new Date().toISOString(),
        HeadlineImageUrl: '',
        Author: '',
        LastModified: new Date(),
        Enabled: false,
        Categories: []
    });
    const [seoWarnings, setSeoWarnings] = useState<string[]>([]);
    const [keywordInput, setKeywordInput] = useState<string>('');
    const [keywords, setKeywords] = useState<string[]>([]);
    const [readabilityScore, setReadabilityScore] = useState<{ score: number, level: string }>({ score: 0, level: 'Not calculated' });
    const [pendingImageBlock, setPendingImageBlock] = useState<number | null>(null);
    const [pasteArticleDialogOpen, setPasteArticleDialogOpen] = useState(false);
    const [pastedArticleText, setPastedArticleText] = useState('');
    const [imagePlacementFrequency, setImagePlacementFrequency] = useState(2);
    const [availableCategories, setAvailableCategories] = useState<string[]>([]);
    const [newCategory, setNewCategory] = useState('');
    const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
    const [hoveredBlockIndex, setHoveredBlockIndex] = useState<number | null>(null);
    const [focusedBlockIndex, setFocusedBlockIndex] = useState<number | null>(null);
    const [showAddMenu, setShowAddMenu] = useState<number | null>(null);
    const headlineImageInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        const defaultKeywords = [
            "birthday party ideas",
            "christmas party games",
            "hall rentals near me",
            "40th birthday ideas",
            "party themes",
            "halls near me",
            "30th birthday ideas",
            "50th birthday ideas",
            "christmas activities",
            "party halls near me",
            "new year's eve party ideas",
            "21st birthday ideas",
            "halls for rent",
            "warehouse live",
            "60th birthday ideas",
            "restaurants with party rooms",
        ];
        const extractedKeywords = defaultKeywords.map(keyword => keyword.replace(/\s*\(.*\)/, '').trim());
        setKeywords(extractedKeywords);
    }, []);

    useEffect(() => {
        const loadVenues = async () => {
            try {
                const venueData = await FetchAllVenues(100, null, true);
                setVenues(venueData.venues);
            } catch (error) {
                console.error('Error loading venues:', error);
            }
        };
        loadVenues();
    }, []);

    // Load user roles when user is available
    useEffect(() => {
        const loadUserRoles = async () => {
            if (user) {
                try {
                    const roles = await getUserRoles(user);
                    setUserRoles(roles);
                } catch (error) {
                    console.error('Error loading user roles:', error);
                    setError('Error loading user permissions');
                }
            }
        };

        if (!authLoading) {
            loadUserRoles();
        }
    }, [user, authLoading]);

    useEffect(() => {
        const loadBlogPost = async () => {
            if (blogId && !isNewPost) {
                setLoading(true);
                setError(null);
                try {
                    const existingPost = await getBlogPost(blogId);
                    if (existingPost) {
                        const formattedPost = {
                            ...existingPost,
                            PublishDate: existingPost.PublishDate || new Date().toISOString(),
                            LastModified: existingPost.LastModified instanceof Date ?
                                existingPost.LastModified : new Date(existingPost.LastModified),
                            Enabled: existingPost.Enabled !== undefined ? existingPost.Enabled : true
                        };
                        setPost(formattedPost);
                    } else {
                        setError('Blog post not found');
                    }
                } catch (error) {
                    setError('Failed to load blog post. Please try again.');
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };

        loadBlogPost();
    }, [blogId, isNewPost]);

    const addContentBlock = (type: 'text' | 'images' | 'title' | 'button', insertIndex?: number) => {
        const newBlock = {
            Type: type,
            ID: Date.now().toString(),
            ...(type === 'text' && { Text: '' }),
            ...(type === 'title' && { StepTitleWithSubtitle: { Title: '', Subtitle: '' } }),
            ...(type === 'button' && { ButtonLink: { Text: '', Url: '', Variant: 'contained' as const, Color: '#C0C0DD', Size: 'medium' as const } }),
            ...(type === 'images' && { ImageUrls: [], ImageAltTexts: {} })
        };

        const updatedContent = [...post.Content];
        if (insertIndex !== undefined) {
            updatedContent.splice(insertIndex, 0, newBlock);
        } else {
            updatedContent.push(newBlock);
        }
        setPost({ ...post, Content: updatedContent });
        setShowAddMenu(null);

        // Focus the new block
        setTimeout(() => {
            setFocusedBlockIndex(insertIndex !== undefined ? insertIndex : updatedContent.length - 1);
        }, 100);
    };

    const updateContentBlock = (index: number, updatedBlock: any) => {
        const updatedContent = [...post.Content];
        updatedContent[index] = updatedBlock;
        setPost({ ...post, Content: updatedContent });
    };

    const deleteContentBlock = (index: number) => {
        const updatedContent = post.Content.filter((_, i) => i !== index);
        setPost({ ...post, Content: updatedContent });
        setHoveredBlockIndex(null);
        setFocusedBlockIndex(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent, index: number, block: any) => {
        if (e.key === 'Enter' && !e.shiftKey && block.Type === 'text') {
            e.preventDefault();
            addContentBlock('text', index + 1);
        }
        if (e.key === 'Backspace' && block.Type === 'text' && !block.Text) {
            e.preventDefault();
            deleteContentBlock(index);
            if (index > 0) {
                setFocusedBlockIndex(index - 1);
            }
        }
    };

    const renderAddBlockMenu = (insertIndex: number) => (
        <Fade in={showAddMenu === insertIndex}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 1,
                    position: 'relative'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(8px)',
                        borderRadius: '8px',
                        p: 1,
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={() => addContentBlock('text', insertIndex)}
                        sx={{ color: '#C0C0DD' }}
                    >
                        <ArticleIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => addContentBlock('title', insertIndex)}
                        sx={{ color: '#C0C0DD' }}
                    >
                        <TitleIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => addContentBlock('images', insertIndex)}
                        sx={{ color: '#C0C0DD' }}
                    >
                        <ImageIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => addContentBlock('button', insertIndex)}
                        sx={{ color: '#C0C0DD' }}
                    >
                        <LinkIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>
        </Fade>
    );

    const handleImageUpload = async (files: File[], blockIndex: number) => {
        if (!files.length) return;

        const block = post.Content[blockIndex];
        if (block.Type !== 'images') return;

        setUploadingContentImage(blockIndex);
        setContentUploadProgress(prev => ({ ...prev, [blockIndex]: 0 }));

        try {
            const blogIdForUpload = blogId || 'temp';
            const contentId = block.ID || Date.now().toString();

            const handleProgress = (progress: number) => {
                setContentUploadProgress(prev => ({ ...prev, [blockIndex]: progress }));
            };

            const storagePaths = await uploadMultipleBlogImages(
                Array.from(files),
                blogIdForUpload,
                contentId,
                handleProgress
            );

            // Convert storage paths to download URLs
            const storage = getStorage();
            const downloadUrls = await Promise.all(
                storagePaths.map(async (path) => {
                    const storageRef = ref(storage, path);
                    return await getDownloadURL(storageRef);
                })
            );

            // Update the block with new image URLs
            const updatedBlock = {
                ...block,
                ImageUrls: [...(block.ImageUrls || []), ...downloadUrls],
                ImageAltTexts: {
                    ...block.ImageAltTexts,
                    ...downloadUrls.reduce((acc, url, index) => {
                        acc[url] = `Image ${((block.ImageUrls || []).length) + index + 1}`;
                        return acc;
                    }, {} as { [key: string]: string })
                }
            };

            updateContentBlock(blockIndex, updatedBlock);

        } catch (error) {
            console.error('Error uploading images:', error);
            setError('Failed to upload images. Please try again.');
        } finally {
            setUploadingContentImage(null);
            setContentUploadProgress(prev => ({ ...prev, [blockIndex]: 0 }));
        }
    };

    const handleImageDelete = async (blockIndex: number, imageUrl: string) => {
        const block = post.Content[blockIndex];
        if (block.Type !== 'images') return;

        try {
            // Remove from Firebase Storage
            if (blogId) {
                await deleteBlogImage(blogId, imageUrl);
            }

            // Update the block
            const updatedImageUrls = (block.ImageUrls || []).filter(url => url !== imageUrl);
            const updatedImageAltTexts = { ...block.ImageAltTexts };
            delete updatedImageAltTexts[imageUrl];

            const updatedBlock = {
                ...block,
                ImageUrls: updatedImageUrls,
                ImageAltTexts: updatedImageAltTexts
            };

            updateContentBlock(blockIndex, updatedBlock);

        } catch (error) {
            console.error('Error deleting image:', error);
            setError('Failed to delete image. Please try again.');
        }
    };

    const handleSelectVenueImages = () => {
        if (selectedVenueImages.length === 0 || pendingImageBlock === null) return;

        const block = post.Content[pendingImageBlock];
        if (block.Type !== 'images') return;

        const updatedBlock = {
            ...block,
            ImageUrls: [...(block.ImageUrls || []), ...selectedVenueImages],
            ImageAltTexts: {
                ...block.ImageAltTexts,
                ...selectedVenueImages.reduce((acc, url, index) => {
                    acc[url] = `Venue image ${((block.ImageUrls || []).length) + index + 1}`;
                    return acc;
                }, {} as { [key: string]: string })
            }
        };

        updateContentBlock(pendingImageBlock, updatedBlock);
        setSelectImageDialogOpen(false);
        setSelectedVenueImages([]);
        setPendingImageBlock(null);
    };

    const handleHeadlineImageUpload = async (files: File[]) => {
        if (!files.length) return;

        const file = files[0]; // Only take the first file for headline image
        setUploadingHeadlineImage(true);
        setHeadlineUploadProgress(0);

        try {
            const blogIdForUpload = blogId || 'temp';

            const handleProgress = (progress: number) => {
                setHeadlineUploadProgress(progress);
            };

            const storagePath = await uploadBlogImage(
                file,
                blogIdForUpload,
                'headline',
                handleProgress
            );

            // Convert storage path to download URL
            const storage = getStorage();
            const storageRef = ref(storage, storagePath);
            const downloadUrl = await getDownloadURL(storageRef);

            // Update the post with new headline image URL
            setPost({ ...post, HeadlineImageUrl: downloadUrl });

        } catch (error) {
            console.error('Error uploading headline image:', error);
            setError('Failed to upload headline image. Please try again.');
        } finally {
            setUploadingHeadlineImage(false);
            setHeadlineUploadProgress(0);
        }
    };

    const handleHeadlineImageDelete = async () => {
        if (!post.HeadlineImageUrl) return;

        try {
            // Remove from Firebase Storage
            if (blogId) {
                await deleteBlogImage(blogId, post.HeadlineImageUrl);
            }

            // Update the post
            setPost({ ...post, HeadlineImageUrl: '' });

        } catch (error) {
            console.error('Error deleting headline image:', error);
            setError('Failed to delete headline image. Please try again.');
        }
    };

    const renderContentBlock = (block: any, index: number) => {
        const isHovered = hoveredBlockIndex === index;
        const isFocused = focusedBlockIndex === index;
        const showControls = isHovered || isFocused;
        const isUploading = uploadingContentImage === index;
        const uploadProgress = contentUploadProgress[index] || 0;

        return (
            <Box key={block.ID || index}>
                {/* Add block button between blocks */}
                <Box
                    sx={{
                        position: 'relative',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover .add-block-btn': {
                            opacity: 1
                        }
                    }}
                    onClick={() => setShowAddMenu(showAddMenu === index ? null : index)}
                >
                    <IconButton
                        className="add-block-btn"
                        size="small"
                        sx={{
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            color: '#C0C0DD',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.2)'
                            }
                        }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                    {showAddMenu === index && renderAddBlockMenu(index)}
                </Box>

                {/* Content Block */}
                <Box
                    onMouseEnter={() => setHoveredBlockIndex(index)}
                    onMouseLeave={() => setHoveredBlockIndex(null)}
                    sx={{
                        position: 'relative',
                        minHeight: '48px',
                        mb: 1,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            transform: 'translateX(4px)'
                        }
                    }}
                >
                    {/* Block Controls */}
                    <Fade in={showControls}>
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '-48px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                zIndex: 10
                            }}
                        >
                            <IconButton
                                size="small"
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.4)',
                                    '&:hover': { color: 'rgba(255, 255, 255, 0.8)' }
                                }}
                            >
                                <DragIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => deleteContentBlock(index)}
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.4)',
                                    '&:hover': { color: '#FF385C' }
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Fade>

                    {/* Block Content */}
                    {block.Type === 'text' && (
                        <Box
                            contentEditable
                            suppressContentEditableWarning
                            onBlur={(e) => {
                                updateContentBlock(index, { ...block, Text: e.target.textContent || '' });
                                setFocusedBlockIndex(null);
                            }}
                            onFocus={() => setFocusedBlockIndex(index)}
                            onKeyDown={(e) => handleKeyDown(e, index, block)}
                            sx={{
                                minHeight: '1.5em',
                                padding: '12px 16px',
                                fontSize: '16px',
                                lineHeight: 1.6,
                                color: 'white',
                                cursor: 'text',
                                outline: 'none',
                                borderRadius: '4px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.02)'
                                },
                                '&:focus': {
                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                    boxShadow: '0 0 0 2px rgba(192, 192, 221, 0.3)'
                                },
                                '&:empty::before': {
                                    content: '"Type something..."',
                                    color: 'rgba(255, 255, 255, 0.4)',
                                    fontStyle: 'italic'
                                }
                            }}
                            dangerouslySetInnerHTML={{ __html: block.Text || '' }}
                        />
                    )}

                    {block.Type === 'title' && (
                        <Box sx={{ py: 2 }}>
                            <Box
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => {
                                    updateContentBlock(index, {
                                        ...block,
                                        StepTitleWithSubtitle: {
                                            ...block.StepTitleWithSubtitle,
                                            Title: e.target.textContent || ''
                                        }
                                    });
                                }}
                                onFocus={() => setFocusedBlockIndex(index)}
                                sx={{
                                    fontSize: '32px',
                                    fontWeight: 700,
                                    color: 'white',
                                    cursor: 'text',
                                    outline: 'none',
                                    padding: '8px 0',
                                    borderRadius: '4px',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.02)'
                                    },
                                    '&:focus': {
                                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                                        boxShadow: '0 0 0 2px rgba(192, 192, 221, 0.3)'
                                    },
                                    '&:empty::before': {
                                        content: '"Heading..."',
                                        color: 'rgba(255, 255, 255, 0.4)',
                                        fontStyle: 'italic'
                                    }
                                }}
                                dangerouslySetInnerHTML={{ __html: block.StepTitleWithSubtitle?.Title || '' }}
                            />
                            <Box
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => {
                                    updateContentBlock(index, {
                                        ...block,
                                        StepTitleWithSubtitle: {
                                            ...block.StepTitleWithSubtitle,
                                            Subtitle: e.target.textContent || ''
                                        }
                                    });
                                }}
                                sx={{
                                    fontSize: '18px',
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    cursor: 'text',
                                    outline: 'none',
                                    padding: '4px 0',
                                    borderRadius: '4px',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.02)'
                                    },
                                    '&:focus': {
                                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                                        boxShadow: '0 0 0 2px rgba(192, 192, 221, 0.3)'
                                    },
                                    '&:empty::before': {
                                        content: '"Subtitle..."',
                                        color: 'rgba(255, 255, 255, 0.4)',
                                        fontStyle: 'italic'
                                    }
                                }}
                                dangerouslySetInnerHTML={{ __html: block.StepTitleWithSubtitle?.Subtitle || '' }}
                            />
                            <Box
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => {
                                    updateContentBlock(index, {
                                        ...block,
                                        StepTitleWithSubtitle: {
                                            ...block.StepTitleWithSubtitle,
                                            Url: e.target.textContent || ''
                                        }
                                    });
                                }}
                                sx={{
                                    fontSize: '14px',
                                    color: 'rgba(255, 255, 255, 0.6)',
                                    cursor: 'text',
                                    outline: 'none',
                                    padding: '4px 0',
                                    fontFamily: 'monospace',
                                    marginTop: '8px',
                                    '&:focus': {
                                        color: 'rgba(255, 255, 255, 0.9)'
                                    },
                                    '&:empty::before': {
                                        content: '"Optional URL (e.g., https://example.com)..."',
                                        color: 'rgba(255, 255, 255, 0.4)',
                                        fontStyle: 'italic'
                                    }
                                }}
                                dangerouslySetInnerHTML={{ __html: block.StepTitleWithSubtitle?.Url || '' }}
                            />
                        </Box>
                    )}

                    {block.Type === 'button' && (
                        <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => {
                                    updateContentBlock(index, {
                                        ...block,
                                        ButtonLink: {
                                            ...block.ButtonLink,
                                            Text: e.target.textContent || ''
                                        }
                                    });
                                }}
                                onFocus={() => setFocusedBlockIndex(index)}
                                sx={{
                                    display: 'inline-block',
                                    bgcolor: '#FF385C',
                                    color: 'white',
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    cursor: 'text',
                                    outline: 'none',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    maxWidth: 'fit-content',
                                    '&:focus': {
                                        boxShadow: '0 0 0 2px rgba(192, 192, 221, 0.3)'
                                    },
                                    '&:empty::before': {
                                        content: '"Button text..."',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        fontStyle: 'italic'
                                    }
                                }}
                                dangerouslySetInnerHTML={{ __html: block.ButtonLink?.Text || '' }}
                            />
                            <Box
                                contentEditable
                                suppressContentEditableWarning
                                onBlur={(e) => {
                                    updateContentBlock(index, {
                                        ...block,
                                        ButtonLink: {
                                            ...block.ButtonLink,
                                            Url: e.target.textContent || ''
                                        }
                                    });
                                }}
                                sx={{
                                    fontSize: '14px',
                                    color: 'rgba(255, 255, 255, 0.6)',
                                    cursor: 'text',
                                    outline: 'none',
                                    padding: '4px 0',
                                    fontFamily: 'monospace',
                                    '&:focus': {
                                        color: 'rgba(255, 255, 255, 0.9)'
                                    },
                                    '&:empty::before': {
                                        content: '"https://..."',
                                        color: 'rgba(255, 255, 255, 0.4)',
                                        fontStyle: 'italic'
                                    }
                                }}
                                dangerouslySetInnerHTML={{ __html: block.ButtonLink?.Url || '' }}
                            />
                        </Box>
                    )}

                    {block.Type === 'images' && (
                        <Box sx={{ position: 'relative' }}>
                            {isUploading && (
                                <Box sx={{ mb: 2 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={uploadProgress}
                                        sx={{
                                            bgcolor: 'rgba(255,255,255,0.1)',
                                            '& .MuiLinearProgress-bar': {
                                                bgcolor: '#FF385C'
                                            }
                                        }}
                                    />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', mt: 1 }}>
                                        Uploading images... {Math.round(uploadProgress)}%
                                    </Typography>
                                </Box>
                            )}

                            {block.ImageUrls && block.ImageUrls.length > 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {block.ImageUrls.map((imageUrl: string, imageIndex: number) => (
                                        <Box key={imageIndex} sx={{ position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
                                            <OptimizedImage
                                                src={imageUrl}
                                                width={800}
                                                height={400}
                                                alt={block.ImageAltTexts?.[imageUrl] || `Image ${imageIndex + 1}`}
                                                style={{
                                                    width: '100%',
                                                    height: 'auto',
                                                    maxHeight: '400px',
                                                    objectFit: 'cover'
                                                }}
                                                unoptimized={true}
                                            />
                                            <IconButton
                                                onClick={() => handleImageDelete(index, imageUrl)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    bgcolor: 'rgba(0,0,0,0.7)',
                                                    color: 'white',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0,0,0,0.9)',
                                                        color: '#FF385C'
                                                    }
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}

                                    {/* Add more images button */}
                                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<UploadIcon />}
                                            onClick={() => {
                                                const input = fileInputRefs.current[block.ID];
                                                if (input) input.click();
                                            }}
                                            disabled={isUploading}
                                            sx={{
                                                color: '#C0C0DD',
                                                borderColor: 'rgba(224, 177, 241, 0.5)',
                                                '&:hover': {
                                                    borderColor: '#C0C0DD',
                                                    bgcolor: 'rgba(224, 177, 241, 0.1)'
                                                }
                                            }}
                                        >
                                            Add More Images
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ImageIcon />}
                                            onClick={() => {
                                                setPendingImageBlock(index);
                                                setSelectImageDialogOpen(true);
                                            }}
                                            sx={{
                                                color: '#C0C0DD',
                                                borderColor: 'rgba(224, 177, 241, 0.5)',
                                                '&:hover': {
                                                    borderColor: '#C0C0DD',
                                                    bgcolor: 'rgba(224, 177, 241, 0.1)'
                                                }
                                            }}
                                        >
                                            Select from Venues
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        minHeight: '200px',
                                        border: '2px dashed rgba(255,255,255,0.3)',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            borderColor: 'rgba(255,255,255,0.5)',
                                            bgcolor: 'rgba(255, 255, 255, 0.02)'
                                        }
                                    }}
                                >
                                    <ImageIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.4)', mb: 2 }} />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
                                        Add images to your post
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<UploadIcon />}
                                            onClick={() => {
                                                const input = fileInputRefs.current[block.ID];
                                                if (input) input.click();
                                            }}
                                            disabled={isUploading}
                                            sx={{
                                                bgcolor: '#FF385C',
                                                '&:hover': { bgcolor: '#E61E4D' }
                                            }}
                                        >
                                            Upload Images
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ImageIcon />}
                                            onClick={() => {
                                                setPendingImageBlock(index);
                                                setSelectImageDialogOpen(true);
                                            }}
                                            sx={{
                                                color: '#C0C0DD',
                                                borderColor: 'rgba(224, 177, 241, 0.5)',
                                                '&:hover': {
                                                    borderColor: '#C0C0DD',
                                                    bgcolor: 'rgba(224, 177, 241, 0.1)'
                                                }
                                            }}
                                        >
                                            Select from Venues
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {/* Hidden file input */}
                            <input
                                ref={(el) => { fileInputRefs.current[block.ID] = el; }}
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={(e) => {
                                    if (e.target.files && e.target.files.length > 0) {
                                        handleImageUpload(Array.from(e.target.files), index);
                                    }
                                }}
                                style={{ display: 'none' }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            if (isNewPost || !blogId) {
                // For blog editors, pass their email as author to ensure proper ownership and unpublished status
                const authorEmail = userRoles?.isBlogEditor ? user?.email : undefined;
                const createdPostId = await createBlogPost(post, authorEmail);
                router.push(`/admin/blog_editor/${createdPostId}`);
            } else {
                await updateBlogPost(blogId, post);
                router.push('/admin/blog_editor');
            }
        } catch (error) {
            setError('Failed to save blog post');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        router.push('/admin/blog_editor');
    };

    if (loading || authLoading || !userRoles) {
        return (
            <Container maxWidth="lg" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 6 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    // Check if user has any blog permissions
    if (!userRoles?.isLittyAdmin && !userRoles?.isBlogEditor) {
        return (
            <Container maxWidth="lg" sx={{ py: 6 }}>
                <Typography variant="h6" sx={{ color: 'white', textAlign: 'center' }}>
                    You don't have permission to access the blog editor.
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 6 }}>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
                    {isNewPost ? 'Create New Blog Post' : 'Edit Blog Post'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        onClick={handleCancel}
                        variant="outlined"
                        sx={{
                            color: '#C0C0DD',
                            borderColor: 'rgba(224, 177, 241, 0.5)',
                            '&:hover': {
                                borderColor: '#C0C0DD',
                                bgcolor: 'rgba(224, 177, 241, 0.1)'
                            },
                            textTransform: 'none',
                            borderRadius: '8px'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        sx={{
                            bgcolor: '#FF385C',
                            '&:hover': { bgcolor: '#E61E4D' },
                            borderRadius: '8px',
                            textTransform: 'none'
                        }}
                    >
                        {isNewPost ? 'Create Post' : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {/* Blog Meta Information */}
            <Paper sx={{ p: 4, bgcolor: 'rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(8px)', borderRadius: '12px', mb: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                        fullWidth
                        label="Title"
                        value={post.Title}
                        onChange={(e) => setPost({ ...post, Title: e.target.value })}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: 'white',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Subtitle"
                        value={post.Subtitle}
                        onChange={(e) => setPost({ ...post, Subtitle: e.target.value })}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: 'white',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                        }}
                    />

                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Excerpt"
                        value={post.Excerpt}
                        onChange={(e) => setPost({ ...post, Excerpt: e.target.value })}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: 'white',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Headline Image Alt Text"
                        value={post.HeadlineImageAltText || ''}
                        onChange={(e) => setPost({ ...post, HeadlineImageAltText: e.target.value })}
                        helperText="Describe the headline image for accessibility and SEO"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: 'white',
                                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                            '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.5)' }
                        }}
                    />

                    {userRoles?.isLittyAdmin && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={post.Enabled}
                                    onChange={(e) => setPost({ ...post, Enabled: e.target.checked })}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#FF385C',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#FF385C',
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography sx={{ color: 'white' }}>
                                    Published
                                </Typography>
                            }
                        />
                    )}

                    {userRoles?.isBlogEditor && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, fontStyle: 'italic' }}>
                            Your posts are saved as drafts and will be reviewed before publishing.
                        </Typography>
                    )}

                    {/* Headline Image Section */}
                    <Box sx={{ mt: 2 }}>
                        <Typography sx={{ color: 'white', fontSize: '16px', fontWeight: 600, mb: 2 }}>
                            Headline Image
                        </Typography>

                        {uploadingHeadlineImage && (
                            <Box sx={{ mb: 2 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={headlineUploadProgress}
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.1)',
                                        '& .MuiLinearProgress-bar': {
                                            bgcolor: '#FF385C'
                                        }
                                    }}
                                />
                                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', mt: 1 }}>
                                    Uploading headline image... {Math.round(headlineUploadProgress)}%
                                </Typography>
                            </Box>
                        )}

                        {post.HeadlineImageUrl ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ position: 'relative', maxWidth: '300px', borderRadius: '8px', overflow: 'hidden' }}>
                                    <OptimizedImage
                                        src={post.HeadlineImageUrl}
                                        width={300}
                                        height={150}
                                        alt="Headline Image"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            maxHeight: '150px',
                                            objectFit: 'cover'
                                        }}
                                        unoptimized={true}
                                    />
                                    <IconButton
                                        onClick={handleHeadlineImageDelete}
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            bgcolor: 'rgba(0,0,0,0.7)',
                                            color: 'white',
                                            '&:hover': {
                                                bgcolor: 'rgba(0,0,0,0.9)',
                                                color: '#FF385C'
                                            }
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Button
                                    variant="outlined"
                                    startIcon={<UploadIcon />}
                                    onClick={() => headlineImageInputRef.current?.click()}
                                    disabled={uploadingHeadlineImage}
                                    sx={{
                                        color: '#C0C0DD',
                                        borderColor: 'rgba(224, 177, 241, 0.5)',
                                        '&:hover': {
                                            borderColor: '#C0C0DD',
                                            bgcolor: 'rgba(224, 177, 241, 0.1)'
                                        },
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        maxWidth: 'fit-content'
                                    }}
                                >
                                    Change Headline Image
                                </Button>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    minHeight: '120px',
                                    maxWidth: '300px',
                                    border: '2px dashed rgba(255,255,255,0.3)',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: 'rgba(255,255,255,0.5)',
                                        bgcolor: 'rgba(255, 255, 255, 0.02)'
                                    }
                                }}
                                onClick={() => headlineImageInputRef.current?.click()}
                            >
                                <ImageIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.4)', mb: 1 }} />
                                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', textAlign: 'center' }}>
                                    Click to upload headline image
                                </Typography>
                            </Box>
                        )}

                        <input
                            ref={headlineImageInputRef}
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                                if (e.target.files && e.target.files.length > 0) {
                                    handleHeadlineImageUpload(Array.from(e.target.files));
                                }
                            }}
                            style={{ display: 'none' }}
                        />
                    </Box>
                </Box>
            </Paper>

            {/* Content Editor */}
            <Paper sx={{ p: 6, bgcolor: 'rgba(255, 255, 255, 0.08)', backdropFilter: 'blur(8px)', borderRadius: '12px' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 4 }}>
                    Content
                </Typography>

                <Box sx={{ pl: 6, position: 'relative' }}>
                    {post.Content.map((block, index) => renderContentBlock(block, index))}

                    {/* Final add block button - only show when there are existing content blocks */}
                    {post.Content.length > 0 && (
                        <Box
                            sx={{
                                position: 'relative',
                                height: '48px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover .add-block-btn': {
                                    opacity: 1
                                }
                            }}
                            onClick={() => setShowAddMenu(showAddMenu === post.Content.length ? null : post.Content.length)}
                        >
                            <IconButton
                                className="add-block-btn"
                                size="small"
                                sx={{
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                                    color: '#C0C0DD',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.2)'
                                    }
                                }}
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                            {showAddMenu === post.Content.length && renderAddBlockMenu(post.Content.length)}
                        </Box>
                    )}

                    {post.Content.length === 0 && (
                        <Box
                            sx={{
                                textAlign: 'center',
                                py: 8,
                                cursor: 'pointer',
                                borderRadius: '8px',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.02)'
                                }
                            }}
                            onClick={() => addContentBlock('text')}
                        >
                            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '18px', mb: 2 }}>
                                Start writing your post...
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
                                Click here to add your first block
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* Select Image from Venue Dialog */}
            <SelectImageFromVenue
                open={selectImageDialogOpen}
                venues={venues}
                selectedVenue={selectedVenue}
                selectedImages={selectedVenueImages}
                onClose={() => {
                    setSelectImageDialogOpen(false);
                    setSelectedVenueImages([]);
                    setPendingImageBlock(null);
                }}
                onVenueSelect={(venueId) => {
                    const venue = venues.find(v => v.ID === venueId);
                    setSelectedVenue(venue || null);
                }}
                onImageSelect={(imageUrl) => {
                    setSelectedVenueImages(prev =>
                        prev.includes(imageUrl)
                            ? prev.filter(url => url !== imageUrl)
                            : [...prev, imageUrl]
                    );
                }}
                onConfirm={handleSelectVenueImages}
                postId={blogId || ''}
            />
        </Container>
    );
};

export default BlogEditor; 